// src/components/tutor/tutor_payroll/TutorPayroll.js
import React, { useMemo, useState } from "react";
import { auth, db } from "../../../firebaseConfig";
import {
  collection, query, where, getDocs, orderBy, Timestamp
} from "firebase/firestore";

import "./TutorPayroll.css";

const getDayStamp = (ts) => {
  if (!ts) return 0;
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  const day = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  return day.getTime();
};

const sortRowsByDate = (rows, dir) => {
  const direction = dir === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => {
    const ad = getDayStamp(a.startTime);
    const bd = getDayStamp(b.startTime);
    if (ad !== bd) return (ad - bd) * direction;
    const ast = a.startTime?.toMillis ? a.startTime.toMillis() : 0;
    const bst = b.startTime?.toMillis ? b.startTime.toMillis() : 0;
    return ast - bst;
  });
};

const TutorPayroll = () => {
  const [loading, setLoading] = useState(false);

  // Filters
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Data
  const [records, setRecords] = useState([]);
  const [hasGenerated, setHasGenerated] = useState(false);
  const [listSortDir, setListSortDir] = useState("asc");
  const [summary, setSummary] = useState({
    adminHours: 0, adminPay: 0,
    teachingHours: 0, teachingPay: 0
  });

  const handleGenerate = async () => {
    if (!startDate || !endDate) {
      alert("Please select a date range.");
      return;
    }

    setLoading(true);
    try {
      const startTs = Timestamp.fromDate(new Date(`${startDate} 00:00:00`));
      const endTs = Timestamp.fromDate(new Date(`${endDate} 23:59:59`));

      const q = query(
        collection(db, "payroll_records"),
        where("uid", "==", auth.currentUser.uid), // ✅ SECURITY: Only own records
        where("startTime", ">=", startTs),
        where("startTime", "<=", endTs),
        orderBy("startTime", "asc")
      );

      const snap = await getDocs(q);
      const data = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setRecords(data);
      calculateSummary(data);
      setHasGenerated(true);
    } catch (error) {
      console.error("Error fetching records:", error);
      alert("Error loading data. See console.");
    } finally {
      setLoading(false);
    }
  };

  const calculateSummary = (data) => {
    let adminH = 0, adminP = 0, teachH = 0, teachP = 0;

    data.forEach((r) => {
      // Sum stored values directly
      if (r.type === "admin") {
        adminH += r.hours;
        adminP += r.totalPay;
      } else {
        teachH += r.hours;
        teachP += r.totalPay;
      }
    });

    setSummary({
      adminHours: parseFloat(adminH.toFixed(2)),
      adminPay: parseFloat(adminP.toFixed(2)),
      teachingHours: parseFloat(teachH.toFixed(2)),
      teachingPay: parseFloat(teachP.toFixed(2))
    });
  };

  const handleClear = () => {
    setRecords([]);
    setHasGenerated(false);
    setSummary({ adminHours: 0, adminPay: 0, teachingHours: 0, teachingPay: 0 });
    setStartDate("");
    setEndDate("");
    setListSortDir("asc");
  };

  const sortedRows = useMemo(
    () => sortRowsByDate(records, listSortDir),
    [records, listSortDir]
  );

  const totalPay = summary.adminPay + summary.teachingPay;
  const totalHours = parseFloat((summary.adminHours + summary.teachingHours).toFixed(2));

  return (
    <div className="tpv-page">

      {/* ── Page header ── */}
      <div className="tpv-pageHeader">
        <div className="tpv-pageTitle">Payroll</div>
        <div className="tpv-pageSubtitle">Review your recorded hours and pay for a date range.</div>
      </div>

      {/* ── Controls card ── */}
      <div className="tpv-controlCard">
        <div className="tpv-controlRow">
          <div className="tpv-controlField">
            <label className="tpv-fieldLabel">From</label>
            <input
              type="date"
              className="tpv-dateInput"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>

          <div className="tpv-controlField">
            <label className="tpv-fieldLabel">Up To</label>
            <input
              type="date"
              className="tpv-dateInput"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>

          <div className="tpv-controlActions">
            <button className="tpv-btnPrimary" onClick={handleGenerate} disabled={loading}>
              {loading ? "Loading…" : "Generate"}
            </button>
            <button className="tpv-btnGhost" onClick={handleClear}>
              Clear
            </button>
          </div>
        </div>
      </div>

      {/* ── Pay Summary (only after Generate) ── */}
      {hasGenerated && (
        <div className="tpv-summaryCard">
          <div className="tpv-summaryTiles">
            <div className="tpv-summaryTile">
              <div className="tpv-summaryTileLabel">Admin</div>
              <div className="tpv-summaryTileHours">{summary.adminHours}h</div>
              <div className="tpv-summaryTileValue">${summary.adminPay.toFixed(2)}</div>
            </div>

            <div className="tpv-summaryDivider" />

            <div className="tpv-summaryTile">
              <div className="tpv-summaryTileLabel">Teaching</div>
              <div className="tpv-summaryTileHours">{summary.teachingHours}h</div>
              <div className="tpv-summaryTileValue">${summary.teachingPay.toFixed(2)}</div>
            </div>

            <div className="tpv-summaryDivider" />

            <div className="tpv-summaryTile">
              <div className="tpv-summaryTileLabel">Total Pay</div>
              <div className="tpv-summaryTileHours">{totalHours}h</div>
              <div className="tpv-summaryTileValueTotal">${totalPay.toFixed(2)}</div>
            </div>
          </div>
        </div>
      )}

      {/* ── Records section ── */}
      <div className="tpv-tableSection">
        <div className="tpv-tableTopBar">
          <div className="tpv-tableTitle">
            {hasGenerated ? "Payroll Records" : "My Payroll"}
          </div>
          {hasGenerated && (
            <div className="tpv-pageMeta">
              <strong>{records.length}</strong> record{records.length === 1 ? "" : "s"}
            </div>
          )}
        </div>

        <div className="tpv-tableCard">
          <div className="tpv-tableScroll">
            <table className="tpv-table">
              <thead>
                <tr>
                  <th
                    className="tpv-thSortable"
                    onClick={() => setListSortDir((d) => (d === "desc" ? "asc" : "desc"))}
                  >
                    <span className="tpv-thSortInner">
                      Date
                      <span className="tpv-sortArrow">
                        {listSortDir === "desc" ? "↓" : "↑"}
                      </span>
                    </span>
                  </th>
                  <th>Type</th>
                  <th>Start Time</th>
                  <th>End Time</th>
                  <th>Hrs</th>
                  <th>Rate</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {sortedRows.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="tpv-emptyCell">
                      {loading
                        ? "Loading records…"
                        : hasGenerated
                        ? "No records found for this date range."
                        : "Select a date range and click Generate."}
                    </td>
                  </tr>
                ) : (
                  (() => {
                    let groupCounter = 0;
                    return sortedRows.map((row, index) => {
                      if (index > 0 && row.date !== sortedRows[index - 1].date) {
                        groupCounter++;
                      }
                      const rowClass = groupCounter % 2 !== 0 ? "tpv-row-alt-group" : "";

                      return (
                        <tr key={row.id} className={rowClass}>
                          <td>{row.date}</td>
                          <td>
                            <span className={`tpv-pill ${row.type === "admin" ? "tpv-pillAdmin" : "tpv-pillTeach"}`}>
                              {row.type === "admin" ? "Admin" : "Teaching"}
                            </span>
                          </td>
                          <td>
                            {row.startTime
                              ? row.startTime.toDate().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
                              : "—"}
                          </td>
                          <td>
                            {row.endTime
                              ? row.endTime.toDate().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
                              : "—"}
                          </td>
                          <td>{row.hours}</td>
                          <td>${row.rateUsed}</td>
                          <td className="tpv-strong">
                            ${row.totalPay?.toFixed ? row.totalPay.toFixed(2) : Number(row.totalPay || 0).toFixed(2)}
                          </td>
                        </tr>
                      );
                    });
                  })()
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

    </div>
  );
};

export default TutorPayroll;
